import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { ActivityCard, type PlannedMoment } from "./Today";
import { Empty, Ring, longDate } from "./ui";
import { useI18n } from "../lib/i18n";
import { streakFrom } from "../lib/streak";
import type { Competency, MasteryRow } from "../types";

export function ParentHome({
  guardianName,
  learnerName,
  moments,
  competencies,
  mastery,
  streakDates,
  route,
  catchUp,
  feedback,
  onOpen,
  onEvidence,
  onToggle,
  onGoToMastery,
}: {
  /**
   * Which service this child is on, so an empty day can be explained truthfully.
   *
   * A day is only built automatically for parent-led learners. For the other two
   * routes the timetable comes from the teacher Al Noor is arranging — but the
   * empty state said "once an admin has published activities for this stage",
   * which was simply untrue: Kindergarten has 81 published activities. A family
   * waiting for a teacher was being told to wait for content that already
   * exists.
   */
  route?: string | null;
  guardianName: string;
  learnerName: string;
  moments: PlannedMoment[];
  competencies: Competency[];
  mastery: MasteryRow[];
  /** Days with a tick or a piece of evidence — what the streak counts. */
  streakDates: string[];
  /** The "did you do yesterday's?" question, when there is one to ask. */
  catchUp?: ReactNode;
  /** "New feedback on {child}'s work", when Al Noor has reviewed something. */
  feedback?: ReactNode;
  onOpen: (moment: PlannedMoment) => void;
  /** Both absent for an account that may not record for this child. */
  onEvidence?: (moment: PlannedMoment) => void;
  onToggle?: (itemId: string) => void;
  onGoToMastery: () => void;
}) {
  const { t, locale } = useI18n();
  const done = moments.filter((moment) => moment.item.status === "done").length;
  const progress = moments.length ? Math.round((done / moments.length) * 100) : 0;
  const totalMinutes = moments.reduce((sum, moment) => sum + moment.activity.duration_minutes, 0);
  const streak = streakFrom(streakDates);
  const learningAreas = new Set(moments.map((moment) => moment.activity.area)).size;

  const byCompetency = new Map(competencies.map((competency) => [competency.id, competency]));
  const movers = [...mastery]
    .sort((a, b) => b.percent - a.percent)
    .slice(0, 2)
    .flatMap((row) => {
      const competency = byCompetency.get(row.competency_id);
      return competency ? [{ row, competency }] : [];
    });

  const firstName = guardianName.split(/\s+/)[0] || guardianName;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t("home.morning") : hour < 17 ? t("home.afternoon") : t("home.evening");

  return (
    <>
      <section className="welcome-grid">
        <div className="welcome-copy">
          <div className="date-pill">
            <Icon name="sun" /> {longDate(new Date(), locale)}
          </div>
          {/* The comma and full stop are part of the translated line: Urdu uses
              its own (، and ۔), and hard-coding the ASCII pair read as a typo. */}
          <h1>{t("home.greetingLine", { greeting, name: firstName })}</h1>
          <p>
            {moments.length > 0
              ? t("home.intro", { count: moments.length })
              : t("home.introEmpty")}
          </p>
          <div className="welcome-actions">
            <button
              className="primary"
              onClick={() => document.getElementById("today-plan")?.scrollIntoView({
                behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
              })}
            >
              {t("home.begin")} <Icon name="arrow" />
            </button>
            <button className="soft-button" onClick={() => window.print()}>
              <Icon name="download" size={16} /> {t("home.print")}
            </button>
          </div>
        </div>
        <div className="daily-orbit glass-card">
          <div className="orbit-copy">
            <span>{t("home.rhythm")}</span>
            <strong>{t("home.ofComplete", { done, total: moments.length })}</strong>
            <small>{t("home.momentsComplete")}</small>
          </div>
          <Ring value={progress} size={112} />
          <div className="streak">
            <Icon name="spark" />
            <span>
              <strong>{t("home.streakCount", { count: streak })}</strong>{" "}
              {t("home.streakLabel")}
            </span>
          </div>
        </div>
      </section>

      {catchUp}
      {feedback}

      <div className="day-at-a-glance">
        <div>
          <span className="summary-icon blue"><Icon name="book" size={21} /></span>
          <dl><dt>{t("home.plannedMoments")}</dt><dd>{moments.length.toLocaleString(locale)}</dd></dl>
        </div>
        <div>
          <span className="summary-icon gold"><Icon name="clock" size={21} /></span>
          <dl><dt>{t("home.learningTime")}</dt><dd>{t("today.minutes", { count: totalMinutes })}</dd></dl>
        </div>
        <div>
          <span className="summary-icon mint"><Icon name="leaf" size={21} /></span>
          <dl><dt>{t("home.learningAreas")}</dt><dd>{learningAreas.toLocaleString(locale)}</dd></dl>
        </div>
      </div>

      <section id="today-plan" className="section-block">
        <div className="section-title">
          <div>
            <span className="eyebrow">{t("home.planTitle")}</span>
            <h2>{t("home.todayWith", { name: learnerName })}</h2>
            <p>
              {moments.length > 0
                ? t("home.about", { minutes: totalMinutes })
                : t("home.nothingScheduled")}
            </p>
          </div>
        </div>
        {moments.length === 0 ? (
          <Empty
            icon="calendar"
            title={route && route !== "parent_led" ? t("home.awaitingTeacherTitle") : t("home.emptyTitle")}
            copy={route && route !== "parent_led" ? t("home.awaitingTeacherCopy") : t("home.emptyCopy")}
          />
        ) : (
          <div className="activity-grid">
            {moments.map((moment, index) => (
              <ActivityCard
                key={moment.item.id}
                order={index + 1}
                moment={moment}
                onOpen={() => onOpen(moment)}
                onEvidence={onEvidence ? () => onEvidence(moment) : undefined}
                onToggle={onToggle ? () => onToggle(moment.item.id) : undefined}
              />
            ))}
          </div>
        )}
      </section>

      {/* The printed plan.
          "Print plan" used to call window.print() on a screen layout, so what
          came out was the dashboard with its navigation and progress ring — and
          none of the detail a parent actually needs away from the device. This
          section is hidden on screen and is the only thing that prints: the
          whole day, with what to say and what to look for, on paper. */}
      <section className="print-plan" aria-hidden="true">
        <header>
          <h1>{t("home.todayWith", { name: learnerName })}</h1>
          <p>{longDate(new Date(), locale)}</p>
        </header>
        {moments.map((moment, index) => (
          <article key={moment.item.id}>
            <h2>
              {index + 1}. {moment.activity.title}
            </h2>
            <p className="print-meta">
              {moment.activity.area_label ?? moment.activity.area} · {t("today.minutes", { count: moment.activity.duration_minutes })}
            </p>
            <p>{moment.activity.prompt}</p>
            {moment.activity.say_this && (
              <p>
                <strong>{t("guide.whatToSay")}:</strong> “
                {moment.activity.teaching_language !== "any" &&
                moment.activity.teaching_language !== locale &&
                moment.activity.original?.say_this
                  ? moment.activity.original.say_this
                  : moment.activity.say_this}
                ”
              </p>
            )}
            {moment.activity.observe_for && (
              <p>
                <strong>{t("guide.whatToNotice")}:</strong> {moment.activity.observe_for}
              </p>
            )}
            {moment.activity.child_move && (
              <p>
                <strong>{t("today.child")}:</strong> {moment.activity.child_move}
              </p>
            )}
            {moment.activity.parent_move && (
              <p>
                <strong>{t("today.parent")}:</strong> {moment.activity.parent_move}
              </p>
            )}
            {/* Somewhere to tick, and somewhere to jot what happened. */}
            <p className="print-note">☐ ______________________________________________</p>
          </article>
        ))}
      </section>

      {movers.length > 0 && (
        <section className="two-column">
          <div className="glass-card journey-card">
            <div className="card-top">
              <span className="icon-tile blue">
                <Icon name="trend" />
              </span>
              <button className="text-button" onClick={onGoToMastery}>
                {t("home.viewMap")} <Icon name="arrow" />
              </button>
            </div>
            <h3>{t("home.growing")}</h3>
            <p>{t("home.growingCopy", { name: learnerName, domain: movers[0].competency.domain })}</p>
            {movers.map(({ row, competency }) => (
              <div className="journey-row" key={competency.id}>
                <div>
                  <span>{competency.title}</span>
                  <strong>{t(`level.${row.level}`)}</strong>
                </div>
                <div className={`bar ${row.percent >= 90 ? "green" : ""}`}>
                  <i style={{ width: `${row.percent}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div className="glass-card growth-card">
            <div className="card-top">
              <span className="icon-tile gold">
                <Icon name="spark" />
              </span>
              <span className="status-pill">{t("home.parentPractice")}</span>
            </div>
            <h3>{t("home.yourPractice")}</h3>
            <p>
              {moments.find((moment) => moment.activity.parent_move)?.activity.parent_move ??
                t("home.defaultPractice")}
            </p>
            <div className="practice-line">
              <div>
                <strong>{t("home.waitWarmth")}</strong>
                <small>{t("home.waitWarmthCopy", { name: learnerName })}</small>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
